import React, { useEffect, useRef } from 'react';
import anime from 'animejs';

// Multi-shape anime.js signature grid: hundreds of mixed primitives
// that breathe continuously and ripple outward from the pointer.
const SHAPE_TYPES = ['rect', 'circle', 'triangle', 'plus'];

const ShapeGrid = ({
    density = 36,
    className = '',
    color = '#ff2a2a',
    accent = 'rgba(255,255,255,0.16)',
    interactive = true,
}) => {
    const wrapRef = useRef(null);
    const svgRef = useRef(null);
    const cellsRef = useRef([]);
    const animationRef = useRef(null);
    const dimRef = useRef({ cols: 0, rows: 0 });

    useEffect(() => {
        const wrap = wrapRef.current;
        const svg = svgRef.current;
        if (!wrap || !svg) return;

        const build = () => {
            const rect = wrap.getBoundingClientRect();
            if (!rect.width || !rect.height) return;

            const cols = Math.max(10, Math.floor(rect.width / density));
            const rows = Math.max(8, Math.floor(rect.height / density));
            const stepX = rect.width / cols;
            const stepY = rect.height / rows;
            dimRef.current = { cols, rows, stepX, stepY };

            svg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);
            svg.setAttribute('width', rect.width);
            svg.setAttribute('height', rect.height);
            svg.innerHTML = '';

            const cells = [];
            for (let y = 0; y < rows; y++) {
                for (let x = 0; x < cols; x++) {
                    const cx = x * stepX + stepX / 2;
                    const cy = y * stepY + stepY / 2;
                    const shapeType = SHAPE_TYPES[(x + y) % SHAPE_TYPES.length];
                    let el;
                    if (shapeType === 'rect') {
                        el = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                        el.setAttribute('x', cx - 2);
                        el.setAttribute('y', cy - 2);
                        el.setAttribute('width', 4);
                        el.setAttribute('height', 4);
                    } else if (shapeType === 'circle') {
                        el = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                        el.setAttribute('cx', cx);
                        el.setAttribute('cy', cy);
                        el.setAttribute('r', 2);
                    } else if (shapeType === 'triangle') {
                        el = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
                        el.setAttribute('points', `${cx},${cy - 3} ${cx + 2.6},${cy + 2} ${cx - 2.6},${cy + 2}`);
                    } else {
                        // plus
                        el = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                        el.setAttribute('d', `M${cx - 3} ${cy} h6 M${cx} ${cy - 3} v6`);
                        el.setAttribute('stroke', accent);
                        el.setAttribute('stroke-width', '1');
                        el.setAttribute('fill', 'none');
                    }
                    if (shapeType !== 'plus') el.setAttribute('fill', accent);
                    el.setAttribute('fill-opacity', '0.22');
                    el.setAttribute('data-x', x);
                    el.setAttribute('data-y', y);
                    el.setAttribute('data-cx', cx);
                    el.setAttribute('data-cy', cy);
                    el.style.transformOrigin = `${cx}px ${cy}px`;
                    el.style.transformBox = 'fill-box';
                    svg.appendChild(el);
                    cells.push(el);
                }
            }
            cellsRef.current = cells;

            if (animationRef.current) animationRef.current.pause();

            // Entrance — anime.timeline stagger from center, scale-in
            anime.timeline()
                .add({
                    targets: cells,
                    scale: [0, 1],
                    rotate: [() => anime.random(-120, 120), 0],
                    opacity: [0, 1],
                    easing: 'easeOutElastic(1, .8)',
                    duration: 1400,
                    delay: anime.stagger(15, { grid: [cols, rows], from: 'center' }),
                });

            // Continuous breathing animation
            animationRef.current = anime({
                targets: cells,
                scale: [
                    { value: 1.4, duration: 1200 },
                    { value: 1, duration: 1400 },
                ],
                rotate: [
                    { value: '+=20', duration: 1200 },
                    { value: '-=20', duration: 1400 },
                ],
                fillOpacity: [
                    { value: 0.45, duration: 1200 },
                    { value: 0.18, duration: 1400 },
                ],
                delay: anime.stagger(45, { grid: [cols, rows], from: 'center' }),
                loop: true,
                easing: 'easeInOutSine',
            });
        };

        build();

        const handlePointer = (e) => {
            if (!interactive) return;
            const rect = wrap.getBoundingClientRect();
            const px = e.clientX - rect.left;
            const py = e.clientY - rect.top;
            const { cols, stepX, stepY } = dimRef.current;
            if (!cols) return;
            const fromCol = Math.floor(px / stepX);
            const fromRow = Math.floor(py / stepY);

            anime({
                targets: cellsRef.current,
                scale: (el) => {
                    const cx = parseFloat(el.getAttribute('data-cx'));
                    const cy = parseFloat(el.getAttribute('data-cy'));
                    const d = Math.hypot(cx - px, cy - py);
                    return d < 180 ? 2.6 - (d / 180) * 1.8 : 1;
                },
                fill: (el) => {
                    const cx = parseFloat(el.getAttribute('data-cx'));
                    const cy = parseFloat(el.getAttribute('data-cy'));
                    const d = Math.hypot(cx - px, cy - py);
                    return d < 180 ? color : accent;
                },
                fillOpacity: (el) => {
                    const cx = parseFloat(el.getAttribute('data-cx'));
                    const cy = parseFloat(el.getAttribute('data-cy'));
                    const d = Math.hypot(cx - px, cy - py);
                    return d < 180 ? 0.95 - (d / 180) * 0.7 : 0.18;
                },
                rotate: (el) => anime.random(-180, 180),
                delay: anime.stagger(20, { grid: [dimRef.current.cols, dimRef.current.rows], from: [fromCol, fromRow] }),
                duration: 900,
                easing: 'easeOutCubic',
            });
        };

        if (interactive) wrap.addEventListener('pointermove', handlePointer);
        const ro = new ResizeObserver(() => build());
        ro.observe(wrap);

        return () => {
            if (interactive) wrap.removeEventListener('pointermove', handlePointer);
            if (animationRef.current) animationRef.current.pause();
            ro.disconnect();
        };
    }, [density, color, accent, interactive]);

    return (
        <div ref={wrapRef} className={`absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
            <svg ref={svgRef} className="block w-full h-full" />
        </div>
    );
};

export default ShapeGrid;
